import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { webCheckinService } from '../services/api';
import EditCheckinFormModal from './modals/EditCheckinFormModal';
import { useIsMobile } from '../hooks';
import { AlertTriangle as AlertTriangleIcon, ClipboardList as ClipboardListIcon, CreditCard as CreditCardIcon, FileText as FileTextIcon } from 'lucide-react';
import { usePmsDataRefresh } from '../agent/dataSync';

function WebCheckinManagement({ user }) {
  const isMobile = useIsMobile();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    paymentVerified: '',
    search: '',
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [pagination, setPagination] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [verifyingItems, setVerifyingItems] = useState(new Set());
  const [statistics, setStatistics] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingForm, setDeletingForm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Load forms based on filters
  // options.silent: reload without the loading state (used after agent changes)
  const loadForms = useCallback(async (options) => {
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.paymentVerified) params.paymentVerified = filters.paymentVerified;
      if (filters.search) params.search = filters.search;
      params.page = filters.page;
      params.limit = filters.limit;
      params.sortBy = filters.sortBy;
      params.sortOrder = filters.sortOrder;

      const response = await webCheckinService.listForms(params);
      if (response.success) {
        setForms(response.data.forms || []);
        setPagination(response.data.pagination || null);
      }
    } catch (error) {
      console.error('Failed to load web check-in forms:', error);
      if (!silent) {
        toast.error('Failed to load web check-in forms');
        setForms([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filters]);

  // Load statistics
  const loadStatistics = useCallback(async () => {
    try {
      const response = await webCheckinService.getStatistics();
      if (response.success) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  }, []);

  useEffect(() => {
    loadForms();
    loadStatistics();
  }, [loadForms, loadStatistics]);

  // Check-ins, check-outs and payments by the PMS agent change these forms: reload them in place.
  usePmsDataRefresh(['bookings', 'payments'], () => {
    loadForms({ silent: true });
    loadStatistics();
  });

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  // View form details
  const viewFormDetails = async (bookingId) => {
    try {
      setLoading(true);
      const response = await webCheckinService.getFormByBooking(bookingId);
      if (response.success) {
        setSelectedForm(response.data);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Failed to load form details:', error);
      toast.error('Failed to load form details');
    } finally {
      setLoading(false);
    }
  };

  // Verify payment
  const handleVerifyPayment = async (bookingId, paymentId, isVerified, notes = '') => {
    const itemKey = `payment-${paymentId}`;
    setVerifyingItems(prev => new Set(prev).add(itemKey));
    
    try {
      const response = await webCheckinService.verifyPayment(bookingId, {
        paymentId,
        isVerified,
        notes
      });
      
      if (response.success) {
        toast.success(isVerified ? 'Payment verified successfully' : 'Payment verification removed');
        // Reload form details if modal is open
        if (selectedForm) {
          await viewFormDetails(bookingId);
        }
        // Reload list
        await loadForms();
      }
    } catch (error) {
      console.error('Failed to verify payment:', error);
      toast.error('Failed to verify payment');
    } finally {
      setVerifyingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemKey);
        return newSet;
      });
    }
  };

  // Verify document
  const handleVerifyDocument = async (bookingId, documentId, isVerified, notes = '') => {
    const itemKey = `document-${documentId}`;
    setVerifyingItems(prev => new Set(prev).add(itemKey));
    
    try {
      const response = await webCheckinService.verifyDocument(bookingId, {
        documentId,
        isVerified,
        notes
      });
      
      if (response.success) {
        toast.success(isVerified ? 'Document verified successfully' : 'Document verification removed');
        // Reload form details if modal is open
        if (selectedForm) {
          await viewFormDetails(bookingId);
        }
        // Reload list
        await loadForms();
      }
    } catch (error) {
      console.error('Failed to verify document:', error);
      toast.error('Failed to verify document');
    } finally {
      setVerifyingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemKey);
        return newSet;
      });
    }
  };

  // Close detail modal
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedForm(null);
  };

  // Open edit modal
  const openEditModal = async (bookingId) => {
    try {
      setLoading(true);
      const response = await webCheckinService.getFormByBooking(bookingId);
      if (response.success) {
        setSelectedForm(response.data);
        setShowEditModal(true);
      }
    } catch (error) {
      console.error('Failed to load form for editing:', error);
      toast.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  // Close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedForm(null);
  };

  // Save edited form
  const handleSaveForm = async (data) => {
    try {
      const response = await webCheckinService.updateFormByBooking(selectedForm.bookingId, data);
      
      if (response.success) {
        toast.success('Check-in form updated successfully!');
        closeEditModal();
        // Reload forms list
        await loadForms();
        // Reload statistics
        await loadStatistics();
      }
    } catch (error) {
      console.error('Failed to update form:', error);
      toast.error(error.message || 'Failed to update form');
      throw error;
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { color: '#B68235', bg: '#F3E6CB', label: 'Pending' },
      'IN_PROGRESS': { color: '#1A3E2A', bg: '#E1E9E2', label: 'In Progress' },
      'OTP_PENDING': { color: '#A9613C', bg: '#E4C2AC', label: 'OTP Pending' },
      'COMPLETED': { color: '#1A3E2A', bg: '#E1E9E2', label: 'Completed' },
      'EXPIRED': { color: '#5F6B61', bg: '#EEEBE1', label: 'Expired' },
      'CANCELLED': { color: '#A13F2F', bg: '#E5B3A9', label: 'Cancelled' }
    };
    const config = statusConfig[status] || statusConfig['PENDING'];
    
    return (
      <span style={{
        backgroundColor: config.bg,
        color: config.color,
        padding: '4px 12px',
        borderRadius: '16px',
        fontSize: '12px',
        fontWeight: '600',
        border: `1px solid ${config.color}30`,
        display: 'inline-block'
      }}>
        {config.label}
      </span>
    );
  };

  // Get document type label
  const getDocumentTypeLabel = (type) => {
    const labels = {
      'AADHAAR_FRONT': 'Aadhaar (Front)',
      'AADHAAR_BACK': 'Aadhaar (Back)',
      'PASSPORT': 'Passport',
      'VISA': 'Visa',
      'DRIVING_LICENSE': 'Driving License',
      'VOTER_ID': 'Voter ID',
      'PAN_CARD': 'PAN Card',
      'OTHER': 'Other'
    };
    return labels[type] || type;
  };

  // Get payment mode label
  const getPaymentModeLabel = (mode) => {
    const labels = {
      'UPI': 'UPI',
      'BANK_TRANSFER': 'Bank Transfer',
      'RAZORPAY': 'Razorpay',
      'CASH': 'Cash',
      'CARD': 'Card',
      'OTHER': 'Other'
    };
    return labels[mode] || mode;
  };

  // Delete form (SuperAdmin only)
  const handleDeleteClick = (form) => {
    setDeletingForm(form);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingForm) return;
    setDeleteLoading(true);
    try {
      const response = await webCheckinService.deleteForm(deletingForm.id);
      if (response.success) {
        toast.success('Check-in form deleted successfully');
        setShowDeleteConfirm(false);
        setDeletingForm(null);
        // Close detail modal if open for this form
        if (selectedForm && selectedForm.id === deletingForm.id) {
          closeDetailModal();
        }
        await loadForms();
        await loadStatistics();
      }
    } catch (error) {
      console.error('Failed to delete form:', error);
      toast.error(error.message || 'Failed to delete check-in form');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletingForm(null);
  };

  // Open image preview
  const openImagePreview = (imageUrl, title) => {
    setImagePreview({ url: imageUrl, title });
  };

  // Close image preview
  const closeImagePreview = () => {
    setImagePreview(null);
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: '10px', marginBottom: '16px' }}>
          <div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: isMobile ? '22px' : '28px', fontWeight: '800', color: '#1A3E2A' }}>
              Web Check-in Management
            </h2>
            <p style={{ margin: '0', color: '#5F6B61', fontSize: isMobile ? '14px' : '16px' }}>
              Review and verify guest web check-in submissions
            </p>
          </div>
        </div>

        {/* Statistics */}
        {statistics && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              backgroundColor: '#F5F3EB',
              padding: '16px',
              borderRadius: '12px',
              border: '1px solid #E3DFD3'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1A3E2A', marginBottom: '4px' }}>
                {statistics.checkinForms?.total || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#5F6B61', fontWeight: '600' }}>Total Forms</div>
            </div>
            <div style={{
              backgroundColor: '#F3E6CB',
              padding: '16px',
              borderRadius: '12px',
              border: '1px solid #C9903C'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#B68235', marginBottom: '4px' }}>
                {statistics.checkinForms?.pending || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#684A1F', fontWeight: '600' }}>Pending</div>
            </div>
            <div style={{
              backgroundColor: '#E1E9E2',
              padding: '16px',
              borderRadius: '12px',
              border: '1px solid #1A3E2A'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1A3E2A', marginBottom: '4px' }}>
                {statistics.checkinForms?.inProgress || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#0F2619', fontWeight: '600' }}>In Progress</div>
            </div>
            <div style={{
              backgroundColor: '#E5B3A9',
              padding: '16px',
              borderRadius: '12px',
              border: '1px solid #A13F2F'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#A13F2F', marginBottom: '4px' }}>
                {statistics.payments?.pending || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#5F241B', fontWeight: '600' }}>Payments Pending</div>
            </div>
            <div style={{
              backgroundColor: '#E1E9E2',
              padding: '16px',
              borderRadius: '12px',
              border: '1px solid #1A3E2A'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1A3E2A', marginBottom: '4px' }}>
                {statistics.payments?.verified || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#0F2619', fontWeight: '600' }}>Payments Verified</div>
            </div>
            <div style={{
              backgroundColor: '#E4C2AC',
              padding: '16px',
              borderRadius: '12px',
              border: '1px solid #A9613C'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#A9613C', marginBottom: '4px' }}>
                {statistics.documents?.pending || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#663923', fontWeight: '600' }}>Documents Pending</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '16px',
          padding: isMobile ? '14px' : '20px',
          backgroundColor: '#F5F3EB',
          borderRadius: '12px',
          border: '1px solid #E3DFD3'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
              Search
            </label>
            <input
              type="text"
              placeholder="Guest name, email, phone, booking..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
              <option value="EXPIRED">Expired</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
              Payment Verified
            </label>
            <select
              value={filters.paymentVerified}
              onChange={(e) => handleFilterChange('paymentVerified', e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="">All</option>
              <option value="true">Verified</option>
              <option value="false">Not Verified</option>
            </select>
          </div>
        </div>
      </div>

      {/* Forms List */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      {loading && forms.length === 0 ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          fontSize: '18px',
          color: '#5F6B61'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #E3DFD3',
              borderTop: '4px solid #1A3E2A',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }}></div>
            Loading web check-in forms...
          </div>
        </div>
      ) : forms.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: '#F5F3EB',
          borderRadius: '12px',
          border: '2px dashed #D2CDBF'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}><ClipboardListIcon size="1em" aria-hidden="true" /></div>
          <h3 style={{ margin: '0 0 8px 0', color: '#36433B', fontSize: '18px' }}>
            No web check-in forms found
          </h3>
          <p style={{ color: '#5F6B61', margin: '0', fontSize: '14px' }}>
            {filters.search || filters.status || filters.paymentVerified
              ? 'Try adjusting your filters'
              : 'Forms will appear here when guests submit web check-ins'}
          </p>
        </div>
      ) : (
        <>
          {isMobile ? (
            <div style={{ display: 'grid', gap: '12px' }}>
              {forms.map((form) => (
                <div
                  key={form.id}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '14px',
                    border: '1px solid #E3DFD3',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '10px' }}>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '14px', color: '#1A3E2A' }}>
                        {form.booking?.bookingNumber || 'N/A'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '2px' }}>
                        {form.guestName}
                      </div>
                    </div>
                    {getStatusBadge(form.status)}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                    <div style={{ fontSize: '12px', color: '#36433B' }}>
                      <strong>Check-in:</strong><br />
                      {form.booking?.checkIn ? format(new Date(form.booking.checkIn), 'MMM dd, yyyy') : 'N/A'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#36433B' }}>
                      <strong>Amount:</strong><br />₹{form.booking?.finalAmount || form.booking?.totalPrice || 0}
                    </div>
                    <div style={{ fontSize: '12px', color: '#36433B' }}>
                      <strong>Documents:</strong> {form.documents?.length || 0}
                    </div>
                    <div style={{ fontSize: '12px', color: '#36433B' }}>
                      <strong>Payments:</strong> {form.payments?.length || 0}
                      {form.payAtArrival && (
                        <span style={{
                          marginLeft: '6px',
                          backgroundColor: '#F8F0EA',
                          color: '#7E472C',
                          padding: '1px 6px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: '600',
                          border: '1px solid #E4C2AC'
                        }}>
                          Pay at Arrival
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                    <button
                      onClick={() => viewFormDetails(form.bookingId)}
                      style={{
                        backgroundColor: '#1A3E2A',
                        color: 'white',
                        border: 'none',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => openEditModal(form.bookingId)}
                      style={{
                        backgroundColor: '#1A3E2A',
                        color: 'white',
                        border: 'none',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                      title="Edit form data"
                    >
                      Edit
                    </button>
                    {user?.role === 'SUPERADMIN' && (
                      <button
                        onClick={() => handleDeleteClick(form)}
                        style={{
                          backgroundColor: '#A13F2F',
                          color: 'white',
                          border: 'none',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                        title="Delete form (SuperAdmin only)"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '980px' }}>
              <thead>
                <tr style={{ backgroundColor: '#F5F3EB', borderBottom: '1px solid #E3DFD3' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#36433B', fontSize: '14px' }}>
                    Booking
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#36433B', fontSize: '14px' }}>
                    Guest
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#36433B', fontSize: '14px' }}>
                    Check-in Date
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#36433B', fontSize: '14px' }}>
                    Status
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#36433B', fontSize: '14px' }}>
                    Documents
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#36433B', fontSize: '14px' }}>
                    Payment
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#36433B', fontSize: '14px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {forms.map((form, index) => (
                  <tr
                    key={form.id}
                    style={{
                      borderBottom: index === forms.length - 1 ? 'none' : '1px solid #E3DFD3',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F5F3EB'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <td style={{ padding: '16px' }}>
                      <div style={{ fontWeight: '600', fontSize: '14px', color: '#1A3E2A' }}>
                        {form.booking?.bookingNumber || 'N/A'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '2px' }}>
                        Amount: ₹{form.booking?.finalAmount || form.booking?.totalPrice || 0}
                      </div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ fontWeight: '600', fontSize: '14px', color: '#1A3E2A' }}>
                        {form.guestName}
                      </div>
                      <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '2px' }}>
                        {form.email}
                      </div>
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#1A3E2A' }}>
                      {form.booking?.checkIn ? format(new Date(form.booking.checkIn), 'MMM dd, yyyy') : 'N/A'}
                    </td>
                    <td style={{ padding: '16px' }}>
                      {getStatusBadge(form.status)}
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: form.documentsUploaded ? '#1A3E2A' : '#5F6B61'
                        }}>
                          {form.documents?.length || 0} uploaded
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: form.paymentSubmitted ? '#1A3E2A' : '#5F6B61'
                        }}>
                          {form.payments?.length || 0} submitted
                        </span>
                        {form.paymentVerified ? (
                          <span style={{
                            backgroundColor: '#E1E9E2',
                            color: '#0F2619',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            display: 'inline-block'
                          }}>
                            Verified
                          </span>
                        ) : (
                          <span style={{
                            backgroundColor: '#F3E6CB',
                            color: '#684A1F',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            display: 'inline-block'
                          }}>
                            Pending
                          </span>
                        )}
                        {form.payAtArrival && (
                          <span style={{
                            backgroundColor: '#F8F0EA',
                            color: '#7E472C',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            display: 'inline-block',
                            border: '1px solid #E4C2AC'
                          }}>
                            Pay at Arrival
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => viewFormDetails(form.bookingId)}
                          style={{
                            backgroundColor: '#1A3E2A',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#163626'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#1A3E2A'}
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => openEditModal(form.bookingId)}
                          style={{
                            backgroundColor: '#1A3E2A',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#163626'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#1A3E2A'}
                          title="Edit form data"
                        >
                          Edit
                        </button>
                        {user?.role === 'SUPERADMIN' && (
                          <button
                            onClick={() => handleDeleteClick(form)}
                            style={{
                              backgroundColor: '#A13F2F',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '8px',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#8E3628'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#A13F2F'}
                            title="Delete form (SuperAdmin only)"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '16px',
              marginTop: '24px',
              padding: isMobile ? '12px' : '16px',
              backgroundColor: '#F5F3EB',
              borderRadius: '12px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => handlePageChange(filters.page - 1)}
                disabled={filters.page === 1}
                style={{
                  backgroundColor: filters.page === 1 ? '#E3DFD3' : '#1A3E2A',
                  color: filters.page === 1 ? '#A5A89B' : 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: filters.page === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(filters.page + 1)}
                disabled={filters.page === pagination.totalPages}
                style={{
                  backgroundColor: filters.page === pagination.totalPages ? '#E3DFD3' : '#1A3E2A',
                  color: filters.page === pagination.totalPages ? '#A5A89B' : 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: filters.page === pagination.totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedForm && (
        <FormDetailModal
          form={selectedForm}
          isMobile={isMobile}
          onClose={closeDetailModal}
          onVerifyPayment={handleVerifyPayment}
          onVerifyDocument={handleVerifyDocument}
          verifyingItems={verifyingItems}
          getStatusBadge={getStatusBadge}
          getDocumentTypeLabel={getDocumentTypeLabel}
          getPaymentModeLabel={getPaymentModeLabel}
          openImagePreview={openImagePreview}
        />
      )}

      {/* Edit Form Modal */}
      {showEditModal && selectedForm && (
        <EditCheckinFormModal
          form={selectedForm}
          onClose={closeEditModal}
          onSave={handleSaveForm}
        />
      )}

      {/* Image Preview Modal */}
      {imagePreview && (
        <ImagePreviewModal
          imageUrl={imagePreview.url}
          title={imagePreview.title}
          onClose={closeImagePreview}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#E9DFCE',
            borderRadius: '16px',
            maxWidth: '480px',
            width: '100%',
            padding: '32px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: '#F9EEEB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: '28px'
            }}>
              <AlertTriangleIcon size="1em" aria-hidden="true" />
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700', color: '#1A3E2A' }}>
              Delete Check-in Form
            </h3>
            <p style={{ margin: '0 0 8px 0', color: '#5F6B61', fontSize: '14px', lineHeight: '1.5' }}>
              Are you sure you want to delete this check-in form? This will permanently remove:
            </p>
            <ul style={{
              textAlign: 'left',
              margin: '0 0 16px 0',
              padding: '0 0 0 20px',
              color: '#5F6B61',
              fontSize: '13px',
              lineHeight: '1.8'
            }}>
              <li>All uploaded documents</li>
              <li>All payment proofs</li>
              <li>OTP records</li>
              <li>Notifications</li>
            </ul>
            <div style={{
              backgroundColor: '#F5F3EB',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '24px',
              textAlign: 'left'
            }}>
              <div style={{ fontSize: '13px', color: '#36433B' }}>
                <strong>Booking:</strong> {deletingForm.booking?.bookingNumber || 'N/A'}
              </div>
              <div style={{ fontSize: '13px', color: '#36433B', marginTop: '4px' }}>
                <strong>Guest:</strong> {deletingForm.guestName || 'N/A'}
              </div>
            </div>
            <p style={{ margin: '0 0 24px 0', color: '#A13F2F', fontSize: '13px', fontWeight: '600' }}>
              This action is irreversible.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={handleCancelDelete}
                disabled={deleteLoading}
                style={{
                  backgroundColor: '#EEEBE1',
                  color: '#36433B',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: deleteLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
                style={{
                  backgroundColor: '#A13F2F',
                  color: 'white',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: deleteLoading ? 'not-allowed' : 'pointer',
                  opacity: deleteLoading ? 0.7 : 1
                }}
              >
                {deleteLoading ? 'Deleting...' : 'Delete Form'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

// Form Detail Modal Component
function FormDetailModal({
  form,
  isMobile,
  onClose,
  onVerifyPayment,
  onVerifyDocument,
  verifyingItems,
  getStatusBadge,
  getDocumentTypeLabel,
  getPaymentModeLabel,
  openImagePreview
}) {
  const [notes, setNotes] = useState({});

  const handleNoteChange = (itemKey, value) => {
    setNotes(prev => ({ ...prev, [itemKey]: value }));
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#E9DFCE',
        borderRadius: '16px',
        maxWidth: isMobile ? '100%' : '1000px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
      }}>
        {/* Header */}
        <div style={{
          padding: isMobile ? '14px' : '24px',
          borderBottom: '1px solid #E3DFD3',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '10px',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10
        }}>
          <div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: isMobile ? '20px' : '24px', fontWeight: '700', color: '#1A3E2A' }}>
              Web Check-in Details
            </h3>
            <p style={{ margin: '0', color: '#5F6B61', fontSize: '14px' }}>
              Booking: {form.booking?.bookingNumber || 'N/A'}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#5F6B61',
              padding: '4px 8px',
              borderRadius: '6px'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#EEEBE1'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: isMobile ? '14px' : '24px' }}>
          {/* Guest Information */}
          <div style={{ marginBottom: '32px' }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '700', color: '#1A3E2A' }}>
              Guest Information
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px',
              padding: '16px',
              backgroundColor: '#F5F3EB',
              borderRadius: '12px'
            }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
                  Name
                </label>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A' }}>
                  {form.guestName}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
                  Email
                </label>
                <div style={{ fontSize: '14px', color: '#1A3E2A' }}>
                  {form.email}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
                  Phone
                </label>
                <div style={{ fontSize: '14px', color: '#1A3E2A' }}>
                  {form.phone}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
                  Status
                </label>
                <div>
                  {getStatusBadge(form.status)}
                </div>
              </div>
              {form.expectedArrivalDate && (
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
                    Expected Arrival
                  </label>
                  <div style={{ fontSize: '14px', color: '#1A3E2A' }}>
                    {format(new Date(form.expectedArrivalDate), 'MMM dd, yyyy')}
                    {form.expectedArrivalTime && ` at ${form.expectedArrivalTime}`}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Booking Summary */}
          <div style={{ marginBottom: '32px' }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '700', color: '#1A3E2A' }}>
              Booking Summary
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              padding: '16px',
              backgroundColor: '#F5F3EB',
              borderRadius: '12px'
            }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
                  Check-in
                </label>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A' }}>
                  {form.booking?.checkIn ? format(new Date(form.booking.checkIn), 'MMM dd, yyyy') : 'N/A'}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
                  Check-out
                </label>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A' }}>
                  {form.booking?.checkOut ? format(new Date(form.booking.checkOut), 'MMM dd, yyyy') : 'N/A'}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
                  Total Amount
                </label>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#1A3E2A' }}>
                  ₹{form.booking?.finalAmount || form.booking?.totalPrice || 0}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Proofs */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <h4 style={{ margin: '0', fontSize: '18px', fontWeight: '700', color: '#1A3E2A' }}>
                Payment Proofs ({form.payments?.length || 0})
              </h4>
              {form.payAtArrival && (
                <span style={{
                  backgroundColor: '#F8F0EA',
                  color: '#7E472C',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '700',
                  border: '1px solid #E4C2AC'
                }}>
                  Pay at Arrival
                </span>
              )}
            </div>
            {form.payments && form.payments.length > 0 ? (
              <div style={{ display: 'grid', gap: '16px' }}>
                {form.payments.map((payment) => {
                  const itemKey = `payment-${payment.id}`;
                  const isVerifying = verifyingItems.has(itemKey);
                  
                  return (
                    <div
                      key={payment.id}
                      style={{
                        padding: '16px',
                        backgroundColor: payment.isVerified ? '#E1E9E2' : '#F3E6CB',
                        borderRadius: '12px',
                        border: `1px solid ${payment.isVerified ? '#1A3E2A' : '#C9903C'}`
                      }}
                    >
                      <div style={{ display: 'flex', gap: '16px', flexDirection: isMobile ? 'column' : 'row' }}>
                        {/* Payment Image */}
                        <div
                          onClick={() => openImagePreview(payment.paymentProofUrl, `Payment Proof - ${getPaymentModeLabel(payment.paymentMode)}`)}
                          style={{
                            width: isMobile ? '100%' : '120px',
                            height: isMobile ? '180px' : '120px',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            flexShrink: 0,
                            border: '2px solid rgba(0,0,0,0.1)'
                          }}
                        >
                          <img
                            src={payment.paymentProofUrl}
                            alt="Payment Proof"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </div>

                        {/* Payment Details */}
                        <div style={{ flex: 1 }}>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))',
                            gap: '12px',
                            marginBottom: '12px'
                          }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
                                Amount
                              </label>
                              <div style={{ fontSize: '16px', fontWeight: '700', color: '#1A3E2A' }}>
                                ₹{payment.amount}
                              </div>
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
                                Payment Mode
                              </label>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A' }}>
                                {getPaymentModeLabel(payment.paymentMode)}
                              </div>
                            </div>
                            {payment.transactionId && (
                              <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
                                  Transaction ID
                                </label>
                                <div style={{ fontSize: '12px', color: '#1A3E2A', fontFamily: 'monospace' }}>
                                  {payment.transactionId}
                                </div>
                              </div>
                            )}
                            <div>
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#5F6B61', marginBottom: '4px' }}>
                                Submitted
                              </label>
                              <div style={{ fontSize: '12px', color: '#1A3E2A' }}>
                                {format(new Date(payment.createdAt), 'MMM dd, yyyy HH:mm')}
                              </div>
                            </div>
                          </div>

                          {/* Verification Section */}
                          {payment.isVerified ? (
                            <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '8px' }}>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: '#0F2619', marginBottom: '8px' }}>
                                Verified
                                {payment.verifiedAt && ` on ${format(new Date(payment.verifiedAt), 'MMM dd, yyyy HH:mm')}`}
                              </div>
                              {payment.notes && (
                                <div style={{ fontSize: '13px', color: '#36433B', marginTop: '8px' }}>
                                  <strong>Notes:</strong> {payment.notes}
                                </div>
                              )}
                              <button
                                onClick={() => onVerifyPayment(form.bookingId, payment.id, false, '')}
                                disabled={isVerifying}
                                style={{
                                  marginTop: '12px',
                                  backgroundColor: isVerifying ? '#A5A89B' : '#A13F2F',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 16px',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  cursor: isVerifying ? 'not-allowed' : 'pointer'
                                }}
                              >
                                {isVerifying ? 'Processing...' : 'Remove Verification'}
                              </button>
                            </div>
                          ) : (
                            <div style={{ marginTop: '12px' }}>
                              <textarea
                                placeholder="Add verification notes (optional)"
                                value={notes[itemKey] || ''}
                                onChange={(e) => handleNoteChange(itemKey, e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  border: '1px solid #D2CDBF',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  resize: 'vertical',
                                  minHeight: '60px',
                                  marginBottom: '8px'
                                }}
                              />
                              <button
                                onClick={() => onVerifyPayment(form.bookingId, payment.id, true, notes[itemKey] || '')}
                                disabled={isVerifying}
                                style={{
                                  backgroundColor: isVerifying ? '#A5A89B' : '#1A3E2A',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 16px',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  cursor: isVerifying ? 'not-allowed' : 'pointer'
                                }}
                              >
                                {isVerifying ? 'Verifying...' : 'Verify Payment'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                padding: '32px',
                textAlign: 'center',
                backgroundColor: '#F5F3EB',
                borderRadius: '12px',
                border: '2px dashed #D2CDBF'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}><CreditCardIcon size="1em" aria-hidden="true" /></div>
                <p style={{ margin: '0', color: '#5F6B61', fontSize: '14px' }}>No payment proofs submitted yet</p>
              </div>
            )}
          </div>

          {/* Documents */}
          <div>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '700', color: '#1A3E2A' }}>
              Identity Documents ({form.documents?.length || 0})
            </h4>
            {form.documents && form.documents.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                {form.documents.map((document) => {
                  const itemKey = `document-${document.id}`;
                  const isVerifying = verifyingItems.has(itemKey);
                  
                  return (
                    <div
                      key={document.id}
                      style={{
                        padding: '16px',
                        backgroundColor: document.isVerified ? '#E1E9E2' : '#F3E6CB',
                        borderRadius: '12px',
                        border: `1px solid ${document.isVerified ? '#1A3E2A' : '#C9903C'}`
                      }}
                    >
                      <div
                        onClick={() => openImagePreview(document.fileUrl, getDocumentTypeLabel(document.documentType))}
                        style={{
                          width: '100%',
                          height: '180px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          marginBottom: '12px',
                          border: '2px solid rgba(0,0,0,0.1)'
                        }}
                      >
                        <img
                          src={document.fileUrl}
                          alt={getDocumentTypeLabel(document.documentType)}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#1A3E2A', marginBottom: '4px' }}>
                          {getDocumentTypeLabel(document.documentType)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#5F6B61' }}>
                          Uploaded {format(new Date(document.uploadedAt), 'MMM dd, yyyy HH:mm')}
                        </div>
                      </div>

                      {document.isVerified ? (
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#0F2619', marginBottom: '8px' }}>
                            Verified
                            {document.verifiedAt && ` on ${format(new Date(document.verifiedAt), 'MMM dd')}`}
                          </div>
                          {document.notes && (
                            <div style={{ fontSize: '12px', color: '#36433B', marginBottom: '8px', padding: '8px', backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '6px' }}>
                              <strong>Notes:</strong> {document.notes}
                            </div>
                          )}
                          <button
                            onClick={() => onVerifyDocument(form.bookingId, document.id, false, '')}
                            disabled={isVerifying}
                            style={{
                              width: '100%',
                              backgroundColor: isVerifying ? '#A5A89B' : '#A13F2F',
                              color: 'white',
                              border: 'none',
                              padding: '8px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: isVerifying ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {isVerifying ? 'Processing...' : 'Remove Verification'}
                          </button>
                        </div>
                      ) : (
                        <div>
                          <textarea
                            placeholder="Notes (optional)"
                            value={notes[itemKey] || ''}
                            onChange={(e) => handleNoteChange(itemKey, e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #D2CDBF',
                              borderRadius: '6px',
                              fontSize: '12px',
                              resize: 'vertical',
                              minHeight: '50px',
                              marginBottom: '8px'
                            }}
                          />
                          <button
                            onClick={() => onVerifyDocument(form.bookingId, document.id, true, notes[itemKey] || '')}
                            disabled={isVerifying}
                            style={{
                              width: '100%',
                              backgroundColor: isVerifying ? '#A5A89B' : '#1A3E2A',
                              color: 'white',
                              border: 'none',
                              padding: '8px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: isVerifying ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {isVerifying ? 'Verifying...' : 'Verify Document'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                padding: '32px',
                textAlign: 'center',
                backgroundColor: '#F5F3EB',
                borderRadius: '12px',
                border: '2px dashed #D2CDBF'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}><FileTextIcon size="1em" aria-hidden="true" /></div>
                <p style={{ margin: '0', color: '#5F6B61', fontSize: '14px' }}>No documents uploaded yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Image Preview Modal Component
function ImagePreviewModal({ imageUrl, title, onClose }) {
  const isMobile = useIsMobile();
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div style={{ position: 'relative', maxWidth: isMobile ? '96%' : '90%', maxHeight: '90%' }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: isMobile ? '-48px' : '-40px',
            right: '0',
            backgroundColor: 'white',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#1A3E2A',
            padding: '8px 16px',
            borderRadius: '8px',
            fontWeight: '700'
          }}
        >
          ✕ Close
        </button>
        {title && (
          <div style={{
            position: 'absolute',
            top: isMobile ? '-48px' : '-40px',
            left: '0',
            backgroundColor: 'white',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            color: '#1A3E2A',
            maxWidth: isMobile ? '70%' : 'none',
            whiteSpace: isMobile ? 'nowrap' : 'normal',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {title}
          </div>
        )}
        <img
          src={imageUrl}
          alt={title || 'Preview'}
          style={{
            maxWidth: '100%',
            maxHeight: '80vh',
            borderRadius: '8px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
}

export default WebCheckinManagement;
